// Versicherte Gefahren und Selbstbeteiligungen eines Immobilienvertrags —
// gelesen aus den Vertragsmerkmalen.
//
// Strukturiert erfasst wird das bislang nur beim Immobilienvertrag: je Gefahr
// ein Merkmal `immo.gefahren.courte.<id>_eur` (Versicherungssumme) plus die
// Selbstbeteiligungen gesammelt in `immo.sb.per_gefahr_json`. Verträge anderer
// Sparten liefern hier leer — dort steht in der Mappe „—", statt eine
// Vollständigkeit vorzutäuschen, die die Daten nicht hergeben.
//
// ⚠️ Das Wertfeld heißt am Vertragsmerkmal `contractCharacteristicValue`
// (`ContractCharacteristics`), NICHT `riskCharacteristicValue` wie am Risiko.
// Beide werden hier gelesen: Bestandscode und einzelne Schreibpfade legen die
// Merkmale gelegentlich unter dem Risiko-Feldnamen ab.

import { labelForGefahrId } from "../immobilieContractStep/gefahrenConfig";
import { CCK } from "../immobilieContractStep/characteristicKeys";

export interface VertragCharLike {
  nameBezeichnung?: string | null;
  contractCharacteristicValue?: string | null;
  riskCharacteristicValue?: string | null;
}

/** Eine versicherte Gefahr mit Versicherungssumme und Selbstbeteiligung. */
export interface GefahrZeile {
  /** Katalog-ID (`gefahrenConfig`) — bestimmt auch das Bildzeichen. */
  id: string;
  label: string;
  /** Versicherungssumme in Euro, sofern erfasst. */
  vs: number | null;
  /** Selbstbeteiligung als Klartext („250 €"), sofern erfasst. */
  sb: string | null;
}

const wertVon = (c: VertragCharLike): string =>
  String(c.contractCharacteristicValue ?? c.riskCharacteristicValue ?? "").trim();

export const charValue = (
  chars: VertragCharLike[] | null | undefined,
  key: string,
): string | null => {
  const hit = chars?.find((c) => (c.nameBezeichnung ?? "").trim() === key);
  const v = hit ? wertVon(hit) : "";
  return v || null;
};

/** Deutsche Zahl („1.250.000,50") als Number; 0 und Unlesbares → null. */
const zahl = (roh: string): number | null => {
  const n = Number(roh.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) && n !== 0 ? n : null;
};

/**
 * Die versicherten Gefahren eines Vertrags, alphabetisch.
 *
 * Bewusst NUR die tatsächlich versicherten: Ein Merkmal
 * `immo.gefahren.courte.<id>_eur` entsteht erst, wenn die Gefahr gedeckt ist.
 * Der ganze Katalog untereinander — zwölf Zeilen, davon acht „nicht versichert"
 * — beantwortet die Frage der Mappe („was ist gedeckt?") schlechter als die
 * kurze Liste dessen, was gilt.
 */
export const gefahrenAus = (chars: VertragCharLike[] | null | undefined): GefahrZeile[] => {
  const alle = chars ?? [];
  const sbMap: Record<string, unknown> = (() => {
    const raw = charValue(alle, CCK.sbPerGefahrJson);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  })();

  const zeilen: GefahrZeile[] = [];
  for (const ch of alle) {
    const name = (ch.nameBezeichnung ?? "").trim();
    const match = /^immo\.gefahren\.courte\.(.+)_eur$/.exec(name);
    if (!match) continue;
    const id = match[1];
    const sbRaw = sbMap[id];
    zeilen.push({
      id,
      label: labelForGefahrId(id),
      vs: zahl(wertVon(ch)),
      sb: sbRaw == null || sbRaw === "" ? null : String(sbRaw),
    });
  }
  return zeilen.sort((a, b) => a.label.localeCompare(b.label, "de"));
};
